// UI text of the Copilot widget (Arabic / English). The backend sends category
// labels itself (/copilot/meta); everything else is here.

export const COPILOT_TEXT = {
  en: {
    title: "LUD Copilot",
    subtitle: "Support conversations",
    open: "Open LUD Copilot support chat",
    close: "Minimize chat",
    dismiss: "Dismiss",
    minimize: "Minimize",
    back: "Back to conversations",
    newConversation: "New conversation",
    conversations: "Conversations",
    unread: (n) => (n === 1 ? "1 unread message" : `${n} unread messages`),
    loading: "Loading…",
    loadMore: "Load more",
    loadEarlier: "Load earlier messages",
    retry: "Try again",
    refresh: "Refresh",
    emptyTitle: "No conversations yet",
    emptyBody: "Start a conversation and our team will reply here.",
    emptyGuest: "Conversations you start on this device appear here.",
    listError: "We could not load your conversations.",
    chatError: "We could not load this conversation.",
    notFound: "This conversation is not available.",
    expiredTitle: "Access to this conversation has expired",
    expiredBody: "For your security, guest access expires. You can start a new conversation, or sign in to keep your conversations.",
    removedFromDevice: "Removed from this device",
    offline: "No connection. Check your internet and try again.",
    rateLimited: (s) => `Too many requests. Please try again in ${s || 30} seconds.`,
    serverError: "Something went wrong. Please try again.",
    category: "Category",
    subject: "Subject",
    message: "Message",
    restaurant: "Related restaurant (optional)",
    noRestaurant: "None",
    name: "Full name",
    email: "Email",
    phone: "Phone number",
    attach: "Attach files",
    attachHint: "JPEG, PNG, WebP or PDF, up to 5 MB each, 5 files per message.",
    removeFile: "Remove file",
    discard: "Discard",
    start: "Start conversation",
    starting: "Starting…",
    signedInAs: (name) => `Signed in as ${name}`,
    guestIntro: "You are not signed in. We need your contact details so we can reach you.",
    required: "This field is required",
    subjectShort: "Subject must be at least 3 characters",
    subjectLong: "Subject must be at most 150 characters",
    messageLong: "Message must be at most 5000 characters",
    emailInvalid: "Enter a valid email address",
    phoneInvalid: "Enter a valid phone number",
    nameShort: "Enter your full name",
    fileType: "Only JPEG, PNG, WebP and PDF files are allowed",
    fileSize: "Each file must be 5 MB or smaller",
    fileCount: "You can attach up to 5 files",
    guestSavedTitle: "Your conversation has started",
    guestSavedBody: (reference) =>
      `Your reference is ${reference}. On this device and browser you can come back to this conversation from the chat button for 14 days after your last message. Clearing your browser data, or using another device, removes that access, so keep your reference. Conversations started while signed in are kept on every device, so consider signing in first next time.`,
    guestNote: "Guest conversations are available on this device for 14 days after your last message.",
    continue: "Continue",
    status: { open: "Open", pending: "Waiting for you", resolved: "Resolved" },
    reopenNotice: "This conversation is resolved. Sending a message will reopen it.",
    composerLabel: "Write a message",
    composerPlaceholder: "Write a message…",
    send: "Send",
    sending: "Sending…",
    failed: "Not sent",
    retrySend: "Retry",
    newMessages: "New messages",
    you: "You",
    team: "LUD team",
    assistant: "LUD Assistant",
    system: "Update",
    read: "Read",
    attachmentFailed: "Attachment could not be uploaded",
    sessionChanged: "Your session changed. Conversations were refreshed.",
    duplicateGuest: "This conversation was already created, but its access key can only be shown once. Please start a new conversation.",
    settings: "Notification settings",
    settingsEmail: "Email notifications",
    settingsEmailHint: "Replies, resolutions and assignments are also sent to your email. A confirmation for each new conversation is always sent.",
    settingsInApp: "In-app notifications",
    settingsInAppHint: "Alerts on the Notifications page of LUD.",
    settingsNote: "Emails are notifications only: to reply, use the chat.",
    signIn: "Sign in",
    linkExpired: "This email link has expired or was already used. Open the latest email from us, or sign in to see your conversations.",
    linkNeedsAccess: "To view this conversation, sign in with the account that started it, or open the link from your email in the browser where you started it.",
  },
  ar: {
    title: "مساعد لُد",
    subtitle: "محادثات الدعم",
    open: "فتح محادثة الدعم مع مساعد لُد",
    close: "تصغير المحادثة",
    dismiss: "إغلاق التنبيه",
    minimize: "تصغير",
    back: "العودة إلى المحادثات",
    newConversation: "محادثة جديدة",
    conversations: "المحادثات",
    unread: (n) => (n === 1 ? "رسالة غير مقروءة" : `${n} رسائل غير مقروءة`),
    loading: "جارٍ التحميل…",
    loadMore: "عرض المزيد",
    loadEarlier: "عرض الرسائل الأقدم",
    retry: "إعادة المحاولة",
    refresh: "تحديث",
    emptyTitle: "لا توجد محادثات بعد",
    emptyBody: "ابدأ محادثة وسيرد عليك فريقنا هنا.",
    emptyGuest: "تظهر هنا المحادثات التي تبدؤها من هذا الجهاز.",
    listError: "تعذّر تحميل محادثاتك.",
    chatError: "تعذّر تحميل هذه المحادثة.",
    notFound: "هذه المحادثة غير متاحة.",
    expiredTitle: "انتهت صلاحية الوصول إلى هذه المحادثة",
    expiredBody: "حفاظًا على أمانك تنتهي صلاحية وصول الزائر. يمكنك بدء محادثة جديدة أو تسجيل الدخول للاحتفاظ بمحادثاتك.",
    removedFromDevice: "تمت إزالتها من هذا الجهاز",
    offline: "لا يوجد اتصال. تحقق من الإنترنت وحاول مرة أخرى.",
    rateLimited: (s) => `طلبات كثيرة. حاول مرة أخرى بعد ${s || 30} ثانية.`,
    serverError: "حدث خطأ ما. حاول مرة أخرى.",
    category: "التصنيف",
    subject: "الموضوع",
    message: "الرسالة",
    restaurant: "المطعم المعني (اختياري)",
    noRestaurant: "بدون",
    name: "الاسم الكامل",
    email: "البريد الإلكتروني",
    phone: "رقم الجوال",
    attach: "إرفاق ملفات",
    attachHint: "JPEG أو PNG أو WebP أو PDF، حتى 5 ميجابايت للملف، و5 ملفات للرسالة.",
    removeFile: "إزالة الملف",
    discard: "حذف",
    start: "بدء المحادثة",
    starting: "جارٍ البدء…",
    signedInAs: (name) => `تم تسجيل الدخول باسم ${name}`,
    guestIntro: "أنت غير مسجّل الدخول. نحتاج بيانات التواصل حتى نتمكن من الوصول إليك.",
    required: "هذا الحقل مطلوب",
    subjectShort: "يجب ألا يقل الموضوع عن 3 أحرف",
    subjectLong: "يجب ألا يزيد الموضوع عن 150 حرفًا",
    messageLong: "يجب ألا تزيد الرسالة عن 5000 حرف",
    emailInvalid: "أدخل بريدًا إلكترونيًا صحيحًا",
    phoneInvalid: "أدخل رقم جوال صحيحًا",
    nameShort: "أدخل اسمك الكامل",
    fileType: "الملفات المسموحة: JPEG وPNG وWebP وPDF فقط",
    fileSize: "يجب ألا يزيد حجم الملف عن 5 ميجابايت",
    fileCount: "يمكنك إرفاق 5 ملفات كحد أقصى",
    guestSavedTitle: "بدأت محادثتك",
    guestSavedBody: (reference) =>
      `رقم مرجعك هو ${reference}. يمكنك العودة إلى هذه المحادثة من زر الدردشة على هذا الجهاز وهذا المتصفح لمدة 14 يومًا بعد آخر رسالة. مسح بيانات المتصفح أو استخدام جهاز آخر يزيل هذا الوصول، فاحتفظ برقم المرجع. المحادثات التي تُبدأ بعد تسجيل الدخول تبقى متاحة على كل أجهزتك، فسجّل الدخول أولًا في المرة القادمة.`,
    guestNote: "تتوفر محادثات الزائر على هذا الجهاز لمدة 14 يومًا بعد آخر رسالة.",
    continue: "متابعة",
    status: { open: "مفتوحة", pending: "بانتظار ردّك", resolved: "تم الحل" },
    reopenNotice: "تم حل هذه المحادثة. إرسال رسالة سيعيد فتحها.",
    composerLabel: "اكتب رسالة",
    composerPlaceholder: "اكتب رسالة…",
    send: "إرسال",
    sending: "جارٍ الإرسال…",
    failed: "لم تُرسل",
    retrySend: "إعادة الإرسال",
    newMessages: "رسائل جديدة",
    you: "أنت",
    team: "فريق لُد",
    assistant: "مساعد لُد",
    system: "تحديث",
    read: "مقروءة",
    attachmentFailed: "تعذّر رفع المرفق",
    sessionChanged: "تغيّرت جلستك. تم تحديث المحادثات.",
    duplicateGuest: "تم إنشاء هذه المحادثة مسبقًا، لكن مفتاح الوصول يظهر مرة واحدة فقط. ابدأ محادثة جديدة.",
    settings: "إعدادات الإشعارات",
    settingsEmail: "إشعارات البريد الإلكتروني",
    settingsEmailHint: "تُرسل الردود وإشعارات الحل والإسناد إلى بريدك أيضًا. يُرسل دائمًا تأكيد لكل محادثة جديدة.",
    settingsInApp: "الإشعارات داخل التطبيق",
    settingsInAppHint: "تنبيهات في صفحة التنبيهات في LUD.",
    settingsNote: "رسائل البريد إشعارات فقط: للرد استخدم المحادثة.",
    signIn: "تسجيل الدخول",
    linkExpired: "انتهت صلاحية رابط البريد أو سبق استخدامه. افتح أحدث رسالة وصلتك منا، أو سجّل الدخول لعرض محادثاتك.",
    linkNeedsAccess: "لعرض هذه المحادثة سجّل الدخول بالحساب الذي بدأها، أو افتح الرابط من بريدك في المتصفح الذي بدأت منه المحادثة.",
  },
};

// Used until /copilot/meta answers; the server's own list (filtered by who is asking) replaces it.
export const CATEGORY_FALLBACK = [
  { value: "technical_support", labelEn: "Technical support", labelAr: "الدعم الفني", audience: "public" },
  { value: "billing", labelEn: "Billing", labelAr: "الفواتير", audience: "authenticated" },
  { value: "integration", labelEn: "Integration", labelAr: "الربط والتكامل", audience: "authenticated" },
  { value: "general_inquiry", labelEn: "General inquiry", labelAr: "استفسار عام", audience: "public" },
  { value: "other", labelEn: "Other", labelAr: "أخرى", audience: "public" },
];

export const categoryLabel = (categories, value, lang) => {
  const found = (categories || CATEGORY_FALLBACK).find((c) => c.value === value);
  return found ? (lang === "ar" ? found.labelAr : found.labelEn) : value;
};

// One place that turns any thrown error into a user message.
export function errorMessage(error, t) {
  if (error?.network) return t.offline;
  if (error?.status === 429) return t.rateLimited(error.retryAfter);
  if (error?.status === 400 && error.message) return error.message;
  return t.serverError;
}
